import { useRef, useState, type FormEvent } from "react";
import { Check, LogOut } from "lucide-react";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { BusinessSection } from "@/features/business/components/BusinessSection";
import { LocationSection } from "@/features/business/components/LocationSection";
import type { Business } from "@/features/business/types/businessTypes";
import type { BusinessLocation } from "@/features/business/types/businessLocationTypes";
import { Button } from "@/shared/components/shadcn/Button";
import { Card } from "@/shared/components/ui/Card";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FormPage } from "@/shared/components/ui/FormPage";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { useProfileEditor } from "../hooks/useProfileEditor";
import type { Profile } from "../types/profileTypes";
import { ProfileSection } from "./ProfileSection";
import { UserAvatar } from "./UserAvatar";

const sections = [
  { value: "profile", label: "Profile" },
  { value: "business", label: "Business" },
  { value: "location", label: "Location" },
] as const;

interface ProfileEditorProps {
  profile: Profile;
  business: Business;
  location: BusinessLocation;
  email?: string;
}

export function ProfileEditor({ profile, business, location, email }: ProfileEditorProps) {
  const { signOut } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState("");
  const loggingOut = useRef(false);
  const editor = useProfileEditor(profile, business, location);
  const hasErrors = Object.values(editor.errors).some(Boolean);
  const name = [profile.firstName, profile.lastName].filter(Boolean).join(" ").trim()
    || profile.displayName || "Your profile";

  const handleLogout = async () => {
    if (loggingOut.current || editor.isSaving) return;
    loggingOut.current = true;
    setIsLoggingOut(true);
    setLogoutError("");
    try {
      await signOut();
    } catch (error) {
      setLogoutError(error instanceof Error ? error.message : "Unable to log out. Please try again.");
    } finally {
      loggingOut.current = false;
      setIsLoggingOut(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loggingOut.current || editor.isSaving || editor.changedSectionCount === 0) return;
    const form = event.currentTarget;

    // Unchanged legacy sections must not block an unrelated update.
    for (const { value } of sections) {
      if (!editor.changedSections[value]) continue;
      const fields = form.querySelectorAll<HTMLInputElement | HTMLSelectElement>(
        `[data-profile-section="${value}"] input, [data-profile-section="${value}"] select`
      );
      const invalid = Array.from(fields).find((field) => !field.validity.valid);
      if (invalid) {
        invalid.focus();
        invalid.reportValidity();
        return;
      }
    }

    void editor.saveChanges().then((failedSection) => {
      if (failedSection) {
        requestAnimationFrame(() => form.querySelector('[role="alert"]')?.scrollIntoView({ block: "nearest" }));
      }
    });
  };

  const status = editor.isSaving ? "Saving your changes..."
    : hasErrors ? "Some changes couldn't be saved. Please try again."
    : editor.isSaved ? "All changes saved."
    : editor.changedSectionCount > 0
      ? `Unsaved changes in ${editor.changedSectionCount} section${editor.changedSectionCount === 1 ? "" : "s"}.`
      : "No unsaved changes.";

  return (
    <FormPage
      header={<PageHeader title="Profile" description="Your details, together in one place." />}
      onSubmit={handleSubmit}
      narrow
      actions={
        <>
          <p role="status" className="w-full text-center text-xs text-muted-foreground">{status}</p>
          <Button type="submit" formNoValidate size="touch" disabled={isLoggingOut || editor.isSaving || editor.changedSectionCount === 0}>
            <Check aria-hidden="true" />
            {editor.isSaving ? "Saving..." : "Save all changes"}
          </Button>
        </>
      }
    >
      <Card className="flex items-center gap-3">
        <UserAvatar size="lg" className="shrink-0 rounded-2xl" />
        <div className="min-w-0 flex-1">
          <p className="break-words font-bold">{name}</p>
          <p className="mt-1 break-words text-sm">{business.name}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          disabled={isLoggingOut || editor.isSaving}
          onClick={() => void handleLogout()}
        >
          <LogOut aria-hidden="true" />
          {isLoggingOut ? "Logging out..." : "Logout"}
        </Button>
      </Card>
      {logoutError && <ErrorState title="Couldn't log out" message={logoutError} />}
      {sections.map(({ value, label }) => editor.errors[value] && (
        <ErrorState key={value} title={`Couldn't save ${label.toLowerCase()}`} message={editor.errors[value].message} />
      ))}
      <fieldset disabled={editor.isSaving || isLoggingOut} className="min-w-0 space-y-5">
        <div data-profile-section="profile">
          <ProfileSection value={editor.draft.profile} onChange={(value) => editor.setSection("profile", value)} email={email} />
        </div>
        <div data-profile-section="business">
          <BusinessSection value={editor.draft.business} onChange={(value) => editor.setSection("business", value)} />
        </div>
        <div data-profile-section="location">
          <LocationSection value={editor.draft.location} onChange={(value) => editor.setSection("location", value)} />
        </div>
      </fieldset>
    </FormPage>
  );
}
